import React, { useEffect, useState } from 'react';
import { useProductFinancing } from '../../hooks/useProductFinancing';
import { Briefcase, Users, Mountain, Sparkles, Check, Lock, ShieldCheck, Clock, Truck } from 'lucide-react';
import SectionLabel from '../ui/SectionLabel';

interface ModelCard {
  id: string;
  tag: string;
  tagColor: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  iconBg: string;
  iconColor: string;
  name: string;
  nameColor: string;
  tagline: string;
  features: string[];
  image: string;
  priceBlock: {
    bg: string;
    ring: string;
    left: { kicker: string; primary: string; unit: string };
    right: { kicker: string; line: string };
  };
  cta: { label: string; href: string; style: string };
  popular: boolean;
}

const MODELS: ModelCard[] = [
  {
    id: 'sunu',
    tag: 'PRO',
    tagColor: 'bg-primary-500',
    icon: Briefcase,
    iconBg: 'bg-primary-50',
    iconColor: 'text-primary-600',
    name: 'SUNU',
    nameColor: 'text-primary-600',
    tagline: 'La mobilité professionnelle intelligente.',
    features: ['Idéale pour les professionnels', 'Autonomie jusqu’à 100 km', 'Recharge ultra-rapide (3 min)'],
    image: '/Sunu_proxy_NB.webp',
    priceBlock: {
      bg: 'bg-primary-50/80',
      ring: 'ring-primary-100/80',
      left: { kicker: 'À partir de', primary: '2 000 FCFA', unit: '/ jour' },
      right: { kicker: 'Devenez propriétaire', line: 'en 24 mois' },
    },
    cta: {
      label: 'Réserver maintenant',
      href: '/products/sunu',
      style: 'bg-primary-500 hover:bg-primary-600 text-white shadow-[0_10px_30px_-10px_rgba(24,153,78,0.55)]',
    },
    popular: false,
  },
  {
    id: 'teranga-plus',
    tag: 'PARTICULIER',
    tagColor: 'bg-neutral-900',
    icon: Users,
    iconBg: 'bg-neutral-50',
    iconColor: 'text-neutral-900',
    name: 'TERANGA+',
    nameColor: 'text-neutral-900',
    tagline: 'La moto électrique pour la ville.',
    features: ['Parfaite pour la mobilité urbaine', 'Design moderne et puissant', 'Économique et écologique'],
    image: '/Teranga_Plus_NOIR_NB.webp',
    priceBlock: {
      bg: 'bg-neutral-50/80',
      ring: 'ring-neutral-200/80',
      left: { kicker: 'Réservation', primary: '50 000 FCFA', unit: '' },
      right: { kicker: 'Payez progressivement', line: 'avec PAY MOLO · Solde à la livraison' },
    },
    cta: {
      label: 'Réserver avec 50 000 FCFA',
      href: '/products/teranga-plus',
      style: 'bg-neutral-900 hover:bg-neutral-800 text-white shadow-[0_10px_30px_-10px_rgba(0,0,0,0.55)]',
    },
    popular: true,
  },
  {
    id: 'omega',
    tag: 'RURAL',
    tagColor: 'bg-primary-500',
    icon: Mountain,
    iconBg: 'bg-primary-50',
    iconColor: 'text-primary-600',
    name: 'OMEGA',
    nameColor: 'text-primary-600',
    tagline: 'La puissance électrique pour zones rurales.',
    features: ['Conçue pour les routes rurales', 'Robuste et endurante', 'Grande autonomie'],
    image: '/omega.webp',
    priceBlock: {
      bg: 'bg-primary-50/80',
      ring: 'ring-primary-100/80',
      left: { kicker: 'À partir de', primary: '2 000 FCFA', unit: '/ jour' },
      right: { kicker: 'Devenez propriétaire', line: 'en 24 mois' },
    },
    cta: {
      label: 'Réserver maintenant',
      href: '/products/omega',
      style: 'bg-primary-500 hover:bg-primary-600 text-white shadow-[0_10px_30px_-10px_rgba(24,153,78,0.55)]',
    },
    popular: false,
  },
];

const TRUST_BADGES = [
  { icon: Lock, label: 'Paiement 100% sécurisé' },
  { icon: ShieldCheck, label: 'Réservation prioritaire' },
  { icon: Clock, label: 'SAV disponible 24/7' },
  { icon: Truck, label: 'Livraison partout au Sénégal' },
];

const PreorderModels: React.FC = () => {
  const finMap = useProductFinancing();
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const target = (() => {
      const d = new Date();
      const y = d.getMonth() > 5 || (d.getMonth() === 5 && d.getDate() > 30) ? d.getFullYear() + 1 : d.getFullYear();
      return new Date(`${y}-06-30T23:59:59`).getTime();
    })();
    const id = setInterval(() => {
      const diff = target - new Date().getTime();
      if (diff < 0) {
        clearInterval(id);
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      } else {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        });
      }
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const segments = [
    { value: countdown.days, label: 'J' },
    { value: countdown.hours, label: 'H' },
    { value: countdown.minutes, label: 'M' },
    { value: countdown.seconds, label: 'S' },
  ];

  return (
    <section className="relative overflow-hidden bg-white py-14 sm:py-20 lg:py-24">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-[10%] h-96 w-96 rounded-full bg-primary-500/[0.04] blur-3xl" />
        <div className="absolute bottom-0 right-[8%] h-[28rem] w-[28rem] rounded-full bg-neutral-900/[0.05] blur-3xl" />
      </div>
      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-12 max-w-2xl text-center sm:mb-16">
          <SectionLabel number="04" className="mb-4">
            Prévente exclusive
          </SectionLabel>
          <h2 className="mb-4 text-3xl font-black leading-[1.05] tracking-tight text-neutral-900 sm:text-4xl lg:text-5xl">
            Nos modèles en <span className="text-primary-600">prévente</span>
          </h2>
          <p className="mx-auto max-w-xl text-base leading-relaxed text-neutral-600 sm:text-lg">
            Réservez dès maintenant et bénéficiez d’offres exclusives jusqu’au <span className="font-semibold text-neutral-900">30 juin</span>.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white/80 px-4 py-2 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.1)] backdrop-blur-sm">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-500" />
            </span>
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-neutral-600">Fin dans</span>
            <div className="flex items-baseline gap-1.5">
              {segments.map((seg, o) => (
                <React.Fragment key={o}>
                  <span className="text-sm font-black tabular-nums text-primary-600">
                    {String(seg.value).padStart(2, '0')}
                    <span className="ml-px text-[10px] font-bold text-neutral-400">{seg.label}</span>
                  </span>
                  {o < segments.length - 1 && <span className="text-neutral-300">·</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div
          className="flex gap-4 overflow-x-auto pt-4 pb-4 scrollbar-hide snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0 md:grid md:gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-6 md:overflow-visible md:pb-0 md:pt-4"
          style={{ scrollPaddingLeft: '1rem' }}
        >
          {MODELS.map((m) => {
            const Icon = m.icon;
            return (
              <article
                key={m.id}
                className={`group relative flex shrink-0 snap-center flex-col rounded-3xl bg-white p-6 transition-all duration-500 hover:-translate-y-1 sm:p-7 w-[85%] md:w-auto md:shrink ${
                  m.popular
                    ? 'ring-2 ring-neutral-900 shadow-[0_16px_50px_-16px_rgba(0,0,0,0.3),_0_6px_24px_-12px_rgba(0,0,0,0.08)] hover:shadow-[0_24px_60px_-16px_rgba(0,0,0,0.4)]'
                    : 'ring-1 ring-neutral-200/80 shadow-[0_8px_30px_-15px_rgba(0,0,0,0.1)] hover:shadow-[0_20px_50px_-15px_rgba(0,0,0,0.15)]'
                }`}
              >
                {m.popular && (
                  <div className="absolute -top-3 left-1/2 z-10 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-neutral-900 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white shadow-[0_8px_20px_-4px_rgba(0,0,0,0.5)]">
                      <Sparkles className="h-3 w-3" strokeWidth={2.5} />
                      Populaire
                    </span>
                  </div>
                )}
                <div className="mb-4 flex items-start justify-between">
                  <span className={`inline-flex items-center rounded-full ${m.tagColor} px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white shadow-sm`}>
                    {m.tag}
                  </span>
                  <div className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${m.iconBg}`}>
                    <Icon className={`h-4 w-4 ${m.iconColor}`} strokeWidth={2.5} />
                  </div>
                </div>
                <div className="mb-5">
                  <h3 className={`mb-1 text-3xl font-black tracking-tight ${m.nameColor}`}>{m.name}</h3>
                  <p className="mb-4 text-sm font-bold leading-tight text-neutral-900">{m.tagline}</p>
                  <ul className="space-y-2">
                    {m.features.map((feat, u) => (
                      <li key={u} className="flex items-start gap-2 text-sm text-neutral-700">
                        <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary-100">
                          <Check className="h-2.5 w-2.5 text-primary-700" strokeWidth={3.5} />
                        </span>
                        {feat}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="relative mb-5 flex h-44 items-end justify-center sm:h-52">
                  <div className="absolute bottom-2 left-1/2 z-0 h-4 w-[72%] -translate-x-1/2 rounded-[50%] bg-neutral-900/[0.16] blur-xl" />
                  <div className="absolute bottom-1 left-1/2 z-0 h-2 w-[50%] -translate-x-1/2 rounded-[50%] bg-neutral-900/[0.25] blur-md" />
                  <img
                    src={m.image}
                    alt={`Moto ${m.name}`}
                    className="relative z-10 h-full w-auto max-w-full object-contain transition-transform duration-700 group-hover:scale-105"
                    style={{ mixBlendMode: 'multiply' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/Sunu_proxy_NB.webp';
                    }}
                  />
                </div>
                <div className={`mt-auto mb-4 grid grid-cols-2 gap-3 rounded-2xl ${m.priceBlock.bg} p-4 ring-1 ring-inset ${m.priceBlock.ring}`}>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{m.priceBlock.left.kicker}</p>
                    <div className="mt-0.5 flex items-baseline gap-1">
                      <span className={`text-base font-black tracking-tight ${m.nameColor} sm:text-lg`}>{m.priceBlock.left.primary}</span>
                      {m.priceBlock.left.unit && <span className="text-xs font-semibold text-neutral-700">{m.priceBlock.left.unit}</span>}
                    </div>
                  </div>
                  <div className="border-l border-neutral-200/80 pl-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{m.priceBlock.right.kicker}</p>
                    <p className="mt-0.5 text-xs font-bold leading-tight text-neutral-900">{m.priceBlock.right.line}</p>
                  </div>
                </div>
                {(m.id === 'sunu' || m.id === 'omega') && finMap[m.id] && (
                  <div className="mb-4 flex flex-wrap items-center gap-1.5">
                    {finMap[m.id].paymolo_enabled && (<span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-[11px] font-bold text-neutral-700"><img src="/images/paymolo-logo.png" alt="" className="h-3 w-auto" /> Pay Molo</span>)}
                    {finMap[m.id].paygo_enabled && (<span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-[11px] font-bold text-neutral-700"><img src="/images/paygo-logo.png" alt="" className="h-3 w-auto" /> PayGo</span>)}
                  </div>
                )}
                {m.id === 'teranga-plus' && (
                  <div className="mb-4 flex flex-wrap items-center gap-2.5">
                    <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-neutral-400">Partenaires</span>
                    <img src="/images/paymolo-logo.png" alt="Pay Molo" className="h-5 w-auto" />
                    <img src="/images/prime-logo.png" alt="Prime Sénégal" className="h-3.5 w-auto" />
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <div className="mt-10 rounded-2xl bg-primary-50/40 px-4 py-4 sm:px-6 sm:py-5 lg:mt-12">
          <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4 lg:gap-8">
            {TRUST_BADGES.map((badge, o) => {
              const Icon = badge.icon;
              return (
                <div key={o} className="flex items-center justify-center gap-2.5">
                  <Icon className="h-4 w-4 shrink-0 text-primary-600" strokeWidth={2.5} />
                  <span className="text-xs font-semibold text-neutral-700 sm:text-sm">{badge.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PreorderModels;
